import { useState, useEffect, useCallback, useRef } from 'react';

export interface PerformanceMetrics {
  loadTime: number;
  domContentLoaded: number;
  firstPaint: number | null;
  firstContentfulPaint: number | null;
  largestContentfulPaint: number | null;
  firstInputDelay: number | null;
  cumulativeLayoutShift: number | null;
  memoryUsage: number | null;
  networkType: string;
  effectiveType: string;
  downlink: number;
  rtt: number;
  saveData: boolean;
}

export interface PerformanceBudget {
  maxLoadTime: number;
  maxFirstContentfulPaint: number;
  maxLargestContentfulPaint: number;
  maxCumulativeLayoutShift: number;
  maxFirstInputDelay: number;
  maxMemoryUsage: number;
}

const DEFAULT_BUDGET: PerformanceBudget = {
  maxLoadTime: 3000,
  maxFirstContentfulPaint: 2000,
  maxLargestContentfulPaint: 2500,
  maxCumulativeLayoutShift: 0.1,
  maxFirstInputDelay: 100,
  maxMemoryUsage: 100 * 1024 * 1024 // 100MB
};

export function usePerformance(budget: Partial<PerformanceBudget> = {}) {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [violations, setViolations] = useState<Set<string>>(new Set());
  const [isSlowNetwork, setIsSlowNetwork] = useState(false);
  const [isLowMemory, setIsLowMemory] = useState(false);

  const performanceObserverRef = useRef<PerformanceObserver | null>(null);
  const budgetRef = useRef({ ...DEFAULT_BUDGET, ...budget });

  const checkBudgetViolation = useCallback((metricName: string, value: number, limit: number) => {
    if (value > limit) {
      setViolations(prev => new Set(prev).add(metricName));
      console.warn(`Performance budget violation: ${metricName} (${value}) exceeds limit (${limit})`);
      return true;
    }
    return false;
  }, []);

  const measureInitialLoad = useCallback(() => {
    if (!performance.timing) return;

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const paintEntries = performance.getEntriesByType('paint');

    const firstPaint = paintEntries.find(entry => entry.name === 'first-paint')?.startTime || null;
    const firstContentfulPaint = paintEntries.find(entry => entry.name === 'first-contentful-paint')?.startTime || null;

    const loadTime = navigation.loadEventEnd - navigation.fetchStart;
    const domContentLoaded = navigation.domContentLoadedEventEnd - navigation.fetchStart;

    const newMetrics: PerformanceMetrics = {
      loadTime,
      domContentLoaded,
      firstPaint,
      firstContentfulPaint,
      largestContentfulPaint: null,
      firstInputDelay: null,
      cumulativeLayoutShift: null,
      memoryUsage: null,
      networkType: 'unknown',
      effectiveType: 'unknown',
      downlink: 0,
      rtt: 0,
      saveData: false
    };

    // Check initial budget violations
    checkBudgetViolation('loadTime', loadTime, budgetRef.current.maxLoadTime);
    if (firstContentfulPaint) {
      checkBudgetViolation('firstContentfulPaint', firstContentfulPaint, budgetRef.current.maxFirstContentfulPaint);
    }

    setMetrics(newMetrics);
  }, [checkBudgetViolation]);

  const getNetworkInfo = useCallback(() => {
    if (!('connection' in navigator)) return;

    const connection = (navigator as any).connection;
    const effectiveType = connection.effectiveType;
    const isSlow = effectiveType === 'slow-2g' || effectiveType === '2g' || connection.saveData;

    setIsSlowNetwork(isSlow);

    return {
      networkType: connection.type || 'unknown',
      effectiveType,
      downlink: connection.downlink || 0,
      rtt: connection.rtt || 0,
      saveData: connection.saveData || false
    };
  }, []);

  const getMemoryUsage = useCallback(() => {
    if (!('memory' in performance)) return null;

    const memory = (performance as any).memory;
    const usedMemory = memory.usedJSHeapSize;
    const isLow = usedMemory > budgetRef.current.maxMemoryUsage;

    setIsLowMemory(isLow);
    checkBudgetViolation('memoryUsage', usedMemory, budgetRef.current.maxMemoryUsage);

    return usedMemory;
  }, [checkBudgetViolation]);

  const observeWebVitals = useCallback(() => {
    if (!('PerformanceObserver' in window)) return;

    try {
      // Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        const lcp = lastEntry.startTime;

        setMetrics(prev => prev ? { ...prev, largestContentfulPaint: lcp } : null);
        checkBudgetViolation('largestContentfulPaint', lcp, budgetRef.current.maxLargestContentfulPaint);
      });

      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // First Input Delay
      const fidObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const firstInput = entries[0] as any;
        const fid = firstInput.processingStart - firstInput.startTime;

        setMetrics(prev => prev ? { ...prev, firstInputDelay: fid } : null);
        checkBudgetViolation('firstInputDelay', fid, budgetRef.current.maxFirstInputDelay);
      });

      fidObserver.observe({ entryTypes: ['first-input'] });

      // Cumulative Layout Shift
      const clsObserver = new PerformanceObserver((entryList) => {
        let clsValue = 0;
        for (const entry of entryList.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }

        setMetrics(prev => prev ? { ...prev, cumulativeLayoutShift: clsValue } : null);
        checkBudgetViolation('cumulativeLayoutShift', clsValue, budgetRef.current.maxCumulativeLayoutShift);
      });

      clsObserver.observe({ entryTypes: ['layout-shift'] });

      performanceObserverRef.current = lcpObserver;
    } catch (error) {
      console.warn('Performance Observer not fully supported:', error);
    }
  }, [checkBudgetViolation]);

  const optimizePerformance = useCallback(() => {
    // Apply performance optimizations based on current conditions
    const document = window.document.documentElement;

    if (isSlowNetwork) {
      document.classList.add('slow-network');
      // Reduce image quality, disable animations, etc.
      document.style.setProperty('--animation-duration', '0s');
    } else {
      document.classList.remove('slow-network');
      document.style.removeProperty('--animation-duration');
    }

    if (isLowMemory) {
      document.classList.add('low-memory');
      // Reduce caching, cleanup unused components
      console.log('Low memory mode activated');
    } else {
      document.classList.remove('low-memory');
    }
  }, [isSlowNetwork, isLowMemory]);

  const forceGarbageCollection = useCallback(() => {
    if ('gc' in window) {
      (window as any).gc();
      console.log('Manual garbage collection triggered');
    } else {
      console.log('Manual garbage collection not available');
    }
  }, []);

  const clearCache = useCallback(async () => {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('Cache cleared successfully');
      } catch (error) {
        console.error('Failed to clear cache:', error);
      }
    }
  }, []);

  const getPerformanceReport = useCallback(() => {
    if (!metrics) return null;

    const budget = budgetRef.current;
    const score = {
      loadTime: Math.max(0, 100 - (metrics.loadTime / budget.maxLoadTime) * 100),
      firstContentfulPaint: metrics.firstContentfulPaint
        ? Math.max(0, 100 - (metrics.firstContentfulPaint / budget.maxFirstContentfulPaint) * 100)
        : null,
      largestContentfulPaint: metrics.largestContentfulPaint
        ? Math.max(0, 100 - (metrics.largestContentfulPaint / budget.maxLargestContentfulPaint) * 100)
        : null,
      firstInputDelay: metrics.firstInputDelay
        ? Math.max(0, 100 - (metrics.firstInputDelay / budget.maxFirstInputDelay) * 100)
        : null,
      cumulativeLayoutShift: metrics.cumulativeLayoutShift
        ? Math.max(0, 100 - (metrics.cumulativeLayoutShift / budget.maxCumulativeLayoutShift) * 100)
        : null,
      memoryUsage: metrics.memoryUsage
        ? Math.max(0, 100 - (metrics.memoryUsage / budget.maxMemoryUsage) * 100)
        : null
    };

    const averageScore = Object.values(score)
      .filter(value => value !== null)
      .reduce((sum, value) => sum + value, 0) /
      Object.values(score).filter(value => value !== null).length;

    return {
      metrics,
      budget,
      violations: Array.from(violations),
      scores: score,
      averageScore: Math.round(averageScore),
      recommendations: generateRecommendations(metrics, budget, violations)
    };
  }, [metrics, violations]);

  const generateRecommendations = (
    currentMetrics: PerformanceMetrics,
    budget: PerformanceBudget,
    currentViolations: Set<string>
  ): string[] => {
    const recommendations: string[] = [];

    if (currentViolations.has('loadTime')) {
      recommendations.push('Consider lazy loading non-critical resources and optimizing bundle size');
    }

    if (currentViolations.has('firstContentfulPaint')) {
      recommendations.push('Optimize critical rendering path and reduce server response time');
    }

    if (currentViolations.has('largestContentfulPaint')) {
      recommendations.push('Optimize images and reduce JavaScript execution time');
    }

    if (currentViolations.has('cumulativeLayoutShift')) {
      recommendations.push('Include size attributes for images and videos to prevent layout shifts');
    }

    if (currentViolations.has('firstInputDelay')) {
      recommendations.push('Reduce JavaScript execution time and break up long tasks');
    }

    if (currentViolations.has('memoryUsage')) {
      recommendations.push('Implement memory management and cleanup unused objects');
    }

    if (isSlowNetwork) {
      recommendations.push('Enable data compression and consider Progressive Web App features');
    }

    if (recommendations.length === 0) {
      recommendations.push('Performance looks good! Continue monitoring and optimizing.');
    }

    return recommendations;
  };

  useEffect(() => {
    // Initial measurements
    measureInitialLoad();
    getNetworkInfo();
    getMemoryUsage();

    // Set up observers
    observeWebVitals();

    // Monitor network changes
    const handleConnectionChange = () => {
      const networkInfo = getNetworkInfo();
      if (networkInfo) {
        setMetrics(prev => prev ? { ...prev, ...networkInfo } : null);
      }
    };

    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      connection.addEventListener('change', handleConnectionChange);
    }

    // Monitor memory usage periodically
    const memoryInterval = setInterval(() => {
      const memoryUsage = getMemoryUsage();
      if (memoryUsage) {
        setMetrics(prev => prev ? { ...prev, memoryUsage } : null);
      }
    }, 30000); // Check every 30 seconds

    // Cleanup
    return () => {
      if (performanceObserverRef.current) {
        performanceObserverRef.current.disconnect();
      }

      if ('connection' in navigator) {
        const connection = (navigator as any).connection;
        connection.removeEventListener('change', handleConnectionChange);
      }

      clearInterval(memoryInterval);
    };
  }, [measureInitialLoad, getNetworkInfo, getMemoryUsage, observeWebVitals]);

  useEffect(() => {
    optimizePerformance();
  }, [optimizePerformance]);

  return {
    metrics,
    violations: Array.from(violations),
    isSlowNetwork,
    isLowMemory,
    getPerformanceReport,
    forceGarbageCollection,
    clearCache,
    budget: budgetRef.current
  };
}

// Utility hook for lazy loading components
export function useLazyLoad(threshold: number = 0.1, rootMargin: string = '50px') {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin]);

  return { elementRef, isVisible };
}

// Utility hook for detecting device capabilities
export function useDeviceCapabilities() {
  const [capabilities, setCapabilities] = useState({
    touch: false,
    hover: false,
    highDPI: false,
    lowEnd: false,
    prefersReducedMotion: false,
    prefersDarkMode: false,
    deviceMemory: 0,
    hardwareConcurrency: 0
  });

  useEffect(() => {
    const updateCapabilities = () => {
      setCapabilities({
        touch: 'ontouchstart' in window,
        hover: window.matchMedia('(hover: hover)').matches,
        highDPI: window.matchMedia('(min-resolution: 2dppx)').matches,
        lowEnd: !!(navigator as any).hardwareConcurrency && (navigator as any).hardwareConcurrency < 4,
        prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        prefersDarkMode: window.matchMedia('(prefers-color-scheme: dark)').matches,
        deviceMemory: (navigator as any).deviceMemory || 0,
        hardwareConcurrency: (navigator as any).hardwareConcurrency || 0
      });
    };

    updateCapabilities();

    // Listen for preference changes
    const mediaQueries = [
      window.matchMedia('(prefers-reduced-motion: reduce)'),
      window.matchMedia('(prefers-color-scheme: dark)')
    ];

    mediaQueries.forEach(mq => mq.addEventListener('change', updateCapabilities));

    return () => {
      mediaQueries.forEach(mq => mq.removeEventListener('change', updateCapabilities));
    };
  }, []);

  return capabilities;
}